import "../instrument";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Route, Routes } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, httpLink, splitLink } from "@trpc/client";
import { useState } from "react";

import { ErrorFallback } from "@/components";
import { AppLayout } from "@/layouts/AppLayout";
import { RootLayout } from "@/layouts/RootLayout";
// TEMPORARY: Manual external-sourcing controls disabled. See route below.
// JobExternalSourcingPage,
import { CatchAllPage } from "@/pages/CatchAllPage";
import { CompaniesPage } from "@/pages/CompaniesPage";
import { CompanyPage } from "@/pages/CompanyPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { DemandCompaniesPage } from "@/pages/DemandCompaniesPage";
import { EarningsPage } from "@/pages/EarningsPage";
import { JobCreatePage } from "@/pages/JobCreatePage";
import { JobPage } from "@/pages/JobPage";
import { JobTrackerPage } from "@/pages/JobTrackerPage";
import { LeaderboardPage } from "@/pages/LeaderboardPage";
import { MetricsPage } from "@/pages/MetricsPage";
import { MotivationPage } from "@/pages/MotivationPage";
import { MyAviaryPage } from "@/pages/MyAviaryPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { NotificationsSettingsPage } from "@/pages/NotificationsSettingsPage";
import { OrphanedWorkableJobsPage } from "@/pages/OrphanedWorkableJobsPage";
import { PlacementTrackerPage } from "@/pages/PlacementTrackerPage";
import { PortfolioPage } from "@/pages/PortfolioPage";
import { PostSignInPage } from "@/pages/PostSignInPage";
import { SignInPage } from "@/pages/SignInPage";
import { ToolsCampaignsCreatePage } from "@/pages/ToolsCampaignsCreatePage";
import { ToolsCampaignsEditPage } from "@/pages/ToolsCampaignsEditPage";
import { ToolsCampaignsListPage } from "@/pages/ToolsCampaignsListPage";
import { ToolsCampaignsShowPage } from "@/pages/ToolsCampaignsShowPage";
import { ToolsDemoConfigsCreatePage } from "@/pages/ToolsDemoConfigsCreatePage";
import { ToolsDemoConfigsEditPage } from "@/pages/ToolsDemoConfigsEditPage";
import { ToolsDemoConfigsListPage } from "@/pages/ToolsDemoConfigsListPage";
import { ToolsDemoConfigsShowPage } from "@/pages/ToolsDemoConfigsShowPage";
import { ToolsSignupVariantsCreatePage } from "@/pages/ToolsSignupVariantsCreatePage";
import { ToolsSignupVariantsEditPage } from "@/pages/ToolsSignupVariantsEditPage";
import { ToolsSignupVariantsListPage } from "@/pages/ToolsSignupVariantsListPage";
import { ToolsSignupVariantsShowPage } from "@/pages/ToolsSignupVariantsShowPage";
import { ToolsSadieDashboardPage } from "@/pages/ToolsSadieDashboardPage";
import { ToolsSadieSimulatorPage } from "@/pages/ToolsSadieSimulatorPage";
import { ToolsSadieVoiceCallsPage } from "@/pages/ToolsSadieVoiceCallsPage";
import { ToolsUsersListPage } from "@/pages/ToolsUsersListPage";
import { ToolsVoiceRecordingsListPage } from "@/pages/ToolsVoiceRecordingsListPage";
import { ToolsExploreImagesPage } from "@/pages/ToolsExploreImagesPage";
import { ToolsAssistantPage } from "@/pages/ToolsAssistantPage";
import { PausedJobMoveGateProvider } from "@/contexts/PausedJobMoveGateContext";
import { env } from "@/lib/env";
import { novaClient } from "@/lib/trpc";
import { MobileLayout } from "@/mobile/layouts/MobileLayout";
import { MobileSadieSimulatorPage } from "@/mobile/pages/MobileSadieSimulatorPage";

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: false,
    },
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const errorFallback = ({ resetError }: { resetError: () => void }) => (
  <ErrorFallback resetError={resetError} />
);

// * Please ensure that the `routes` util is updated as new routes are added here.

function App() {
  const [trpcClient] = useState(() =>
    novaClient.createClient({
      links: [
        splitLink({
          // Top-candidate searches are slow; keep them out of the HTTP batch
          // so each request resolves (and renders) independently.
          condition: (op) => op.path === "internal.candidacy.topResults",
          true: httpLink({
            url: `${env.getNovaBaseUrl()}/api/internal/trpc`,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: "include",
              });
            },
          }),
          false: httpBatchLink({
            url: `${env.getNovaBaseUrl()}/api/internal/trpc`,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: "include",
              });
            },
          }),
        }),
      ],
    }),
  );

  return (
    <Sentry.ErrorBoundary fallback={errorFallback}>
      <novaClient.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider
          client={queryClient}
          // TODO: Is this the best place to put the `<QueryClientProvider>`?
        >
          <PausedJobMoveGateProvider>
            <BrowserRouter>
              <Routes>
                <Route element={<RootLayout />}>
                  <Route path="post-sign-in" element={<PostSignInPage />} />
                  <Route path="sign-in" element={<SignInPage />} />
                  <Route path="mobile" element={<MobileLayout />}>
                    <Route
                      path="sadie-simulator"
                      element={<MobileSadieSimulatorPage />}
                    />
                  </Route>
                  <Route element={<AppLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="companies">
                      <Route index element={<CompaniesPage />} />
                      <Route path=":companyId">
                        <Route index element={<CompanyPage />} />
                        <Route path="jobs">
                          <Route path="create" element={<JobCreatePage />} />
                          <Route path=":jobId">
                            <Route index element={<JobPage />} />
                            {/* TEMPORARY: External sourcing tickets are now
                          auto-created on first ACTIVE. The manual control
                          flow is disabled for now. Restore this route and
                          the relevant imports when deemed appropriate. */}
                            {/* <Route
                            path="external-sourcing"
                            element={<JobExternalSourcingPage />}
                          /> */}
                          </Route>
                        </Route>
                      </Route>
                    </Route>
                    <Route
                      path="placement-tracker"
                      element={<PlacementTrackerPage />}
                    />
                    <Route path="leaderboard" element={<LeaderboardPage />} />
                    <Route path="earnings" element={<EarningsPage />} />
                    <Route path="job-health" element={<PortfolioPage />} />
                    <Route path="portfolio" element={<PortfolioPage />} />
                    <Route path="motivation" element={<MotivationPage />} />
                    <Route path="my-aviary" element={<MyAviaryPage />} />
                    <Route
                      path="notifications"
                      element={<NotificationsPage />}
                    />
                    <Route
                      path="notifications/settings"
                      element={<NotificationsSettingsPage />}
                    />
                    <Route path="tools">
                      <Route
                        path="assistant"
                        element={<ToolsAssistantPage />}
                      />
                      <Route path="job-tracker" element={<JobTrackerPage />} />
                      <Route path="campaigns">
                        <Route index element={<ToolsCampaignsListPage />} />
                        <Route
                          path="create"
                          element={<ToolsCampaignsCreatePage />}
                        />
                        <Route path=":id">
                          <Route index element={<ToolsCampaignsShowPage />} />
                          <Route
                            path="edit"
                            element={<ToolsCampaignsEditPage />}
                          />
                        </Route>
                      </Route>
                      <Route path="demand" element={<DemandCompaniesPage />} />
                      <Route path="demo-configs">
                        <Route index element={<ToolsDemoConfigsListPage />} />
                        <Route
                          path="create"
                          element={<ToolsDemoConfigsCreatePage />}
                        />
                        <Route path=":id">
                          <Route index element={<ToolsDemoConfigsShowPage />} />
                          <Route
                            path="edit"
                            element={<ToolsDemoConfigsEditPage />}
                          />
                        </Route>
                      </Route>
                      <Route
                        path="explore-images"
                        element={<ToolsExploreImagesPage />}
                      />
                      <Route path="metrics" element={<MetricsPage />} />
                      <Route
                        path="orphaned-workable-jobs"
                        element={<OrphanedWorkableJobsPage />}
                      />
                      <Route
                        path="sadie-dashboard"
                        element={<ToolsSadieDashboardPage />}
                      />
                      <Route
                        path="sadie-simulator"
                        element={<ToolsSadieSimulatorPage />}
                      />
                      <Route
                        path="sadie-voice-calls"
                        element={<ToolsSadieVoiceCallsPage />}
                      />
                      <Route path="users" element={<ToolsUsersListPage />} />
                      <Route
                        path="voice-recordings"
                        element={<ToolsVoiceRecordingsListPage />}
                      />
                      <Route path="signup-variants">
                        <Route
                          index
                          element={<ToolsSignupVariantsListPage />}
                        />
                        <Route
                          path="create"
                          element={<ToolsSignupVariantsCreatePage />}
                        />
                        <Route path=":id">
                          <Route
                            index
                            element={<ToolsSignupVariantsShowPage />}
                          />
                          <Route
                            path="edit"
                            element={<ToolsSignupVariantsEditPage />}
                          />
                        </Route>
                      </Route>
                    </Route>
                  </Route>
                  <Route path="*" element={<CatchAllPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </PausedJobMoveGateProvider>
        </QueryClientProvider>
      </novaClient.Provider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
